import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    formatAutomationTextForDisplay,
    mergeFileSuggestionLists,
} from '../../../utils/automation/variableUtils';
import { selectFileSuggestions } from '../../../features/automationStudio/automationStudioSelectors';
import useVariables from './useVariables';

/**
 * Shared "stored → display" text resolver for Automation Studio.
 *
 * Pulls `variablesList`, `variablesListDescriptions`, and the merged file-suggestions
 * list from Redux, and returns a stable `format(text)` helper that:
 *   - rewrites `${_id:<uuid>}` → `${ENV.<name>}` / `${PROJECT.<name>}`
 *   - rewrites `@[<fileId>]` → file name
 *
 * Use this hook in any component that displays strings which may contain either
 * of the above stored-form tokens, instead of re-wiring the Redux selectors
 * and calling `formatAutomationTextForDisplay` inline in each caller.
 */
export default function useAutomationTextFormatter() {
    const { variablesList, variablesListDescriptions } = useVariables();
    const automationFileSuggestions = useSelector(selectFileSuggestions) ?? [];
    const replayFileSuggestions = useSelector((s) => s.replay?.fileSuggestions ?? []) ?? [];

    const fileSuggestions = useMemo(
        () => mergeFileSuggestionLists(automationFileSuggestions, replayFileSuggestions),
        [automationFileSuggestions, replayFileSuggestions]
    );

    const format = useCallback(
        (text) => {
            if (text == null) return text;
            return formatAutomationTextForDisplay(
                typeof text === 'string' ? text : String(text),
                variablesList,
                variablesListDescriptions,
                fileSuggestions
            );
        },
        [variablesList, variablesListDescriptions, fileSuggestions]
    );

    return useMemo(
        () => ({
            format,
            variablesList,
            variablesListDescriptions,
            fileSuggestions,
        }),
        [format, variablesList, variablesListDescriptions, fileSuggestions]
    );
}
